import './Filter.styles.css';

import _ from 'lodash';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Chip } from 'primereact/chip';
import { MultiSelect } from 'primereact/multiselect';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { DEFAULT_AUTO_FILTER_OPTIONS } from '../../constants';
import { GameType } from '../../model/enumeration/GameType.enum';
import { Permission } from '../../model/enumeration/Permission.enum';
import authStore from '../../store/AuthStore';
import commonStore from '../../store/CommonStore';
import localStorageStore from '../../store/LocalStorageStore';
import notificationStore from '../../store/NotificationStore';
import permissionStore from '../../store/PermissionStore';
import tableStore from '../../store/TableStore';
import userSettingsStore from '../../store/UserSettingsStore';
import SortedMultiSelect from '../shared/SortedMultiSelect/SortedMultiSelect';
import { FilterOptions, FilterProps } from './Filter.types';

const PLAYERS_DIMENSION = [
  { label: '0', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
  { label: '6', value: 6 },
  { label: '7', value: 7 },
  { label: '8', value: 8 },
  { label: '9', value: 9 },
  { label: '10', value: 10 },
];

const Filter: FC<FilterProps> = ({ toggleEvent, isChipsVisible }) => {
  const overlayRef = useRef(null);

  const [tableId, setTableId] = useState(
    tableStore.selectedFilterOptions.tableId,
  );
  const [gameId, setGameId] = useState(tableStore.selectedFilterOptions.gameId);
  const [playerId, setPlayerId] = useState(
    tableStore.selectedFilterOptions.playerId,
  );
  const [playerClub, setPlayerClub] = useState(
    tableStore.selectedFilterOptions.playerClub,
  );
  const [playerLeague, setPlayerLeague] = useState(
    tableStore.selectedFilterOptions.playerLeague,
  );
  const [playerUniqueId, setPlayerUniqueId] = useState(
    tableStore.selectedFilterOptions.playerUniqueId,
  );
  const [tableName, setTableName] = useState(
    tableStore.selectedFilterOptions.tableName,
  );
  const [tableLimit, setTableLimit] = useState(
    commonStore.convertLimits(tableStore.selectedFilterOptions.tableLimit),
  );
  const [sittingPlayers, setSittingPlayers] = useState(
    tableStore.selectedFilterOptions.sittingPlayers || [],
  );
  const [sittingBots, setSittingBots] = useState(
    tableStore.selectedFilterOptions.sittingBots || [],
  );
  const [sittingLivePlayers, setSittingLivePlayers] = useState(
    tableStore.selectedFilterOptions.sittingLivePlayers || [],
  );

  const [autoFilterOptions, setAutoFilterOptions] = useState<boolean>(
    localStorageStore.getAutoFilterOptionsOrDefault(
      DEFAULT_AUTO_FILTER_OPTIONS,
    ),
  );

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({});

  const hasShowUniqueIdsPermission = useMemo(
    () => permissionStore.hasPermission(Permission.SHOW_UNIQUE_IDS),
    [authStore.authenticatedUser],
  );

  useEffect(() => {
    tableStore.updateFilterOptions({ tableId });
  }, [tableId]);

  useEffect(() => {
    tableStore.updateFilterOptions({ gameId });
  }, [gameId]);

  useEffect(() => {
    tableStore.updateFilterOptions({ playerId });
  }, [playerId]);

  useEffect(() => {
    tableStore.updateFilterOptions({ playerClub });
  }, [playerClub]);

  useEffect(() => {
    tableStore.updateFilterOptions({ playerLeague });
  }, [playerLeague]);

  useEffect(() => {
    tableStore.updateFilterOptions({ playerUniqueId });
  }, [playerUniqueId]);

  useEffect(() => {
    tableStore.updateFilterOptions({ tableName });
  }, [tableName]);

  useEffect(() => {
    tableStore.updateFilterOptions({
      tableLimit: commonStore.revertLimits(tableLimit),
    });
  }, [tableLimit]);

  useEffect(() => {
    tableStore.updateFilterOptions({ sittingPlayers });
  }, [sittingPlayers]);

  useEffect(() => {
    tableStore.updateFilterOptions({ sittingBots });
  }, [sittingBots]);

  useEffect(() => {
    tableStore.updateFilterOptions({ sittingLivePlayers });
  }, [sittingLivePlayers]);

  useEffect(() => {
    setTableLimit(
      commonStore.convertLimits(tableStore.selectedFilterOptions.tableLimit),
    );
  }, [userSettingsStore.settings.table.limsX100]);

  const stopTrackingBot = (botId: string): void => {
    notificationStore.showNotification({
      severity: 'warn',
      summary: 'Warning',
      detail: (
        <>
          The bot (<b>{botId}</b>) stopped to be tracked
        </>
      ),
    });
    tableStore.stopTrackingBot(botId, false);
  };

  const updatePlayerId = (playerIds: string[]): void => {
    setPlayerId(playerIds);
    _.difference(tableStore.trackedBotIds, playerIds).forEach(stopTrackingBot);
  };

  const allGameTypes = useMemo(
    () => Object.keys(GameType).map(key => ({ key, value: GameType[key] })),
    [],
  );

  const staticFilterOptions = useMemo(
    () => tableStore.getStaticFilterOptions(),
    [
      tableStore.filterStateChanged,
      tableStore.activeTables.size,
      hasShowUniqueIdsPermission,
      userSettingsStore.settings.table.limsX100,
    ],
  );

  useEffect(() => {
    if (autoFilterOptions) {
      return;
    }
    setFilterOptions(staticFilterOptions);
  }, [autoFilterOptions, staticFilterOptions]);

  useEffect(() => {
    if (!autoFilterOptions) {
      return;
    }
    setFilterOptions(prevState => {
      const notConsideredProperties = {
        gameTypes: [],
        onlyWithLivePlayers: false,
        onlyBots: false,
        autoOpenTables: false,
        autoCloseTables: false,
        onlyStartedGames: false,
        hideFullTables: false,
      } as FilterOptions;
      if (
        _.isEqual(
          {
            ...tableStore.selectedFilterOptions,
            ...notConsideredProperties,
          },
          {
            ...tableStore.defaultSelectedFilterOptions,
            ...notConsideredProperties,
          },
        )
      ) {
        return tableStore.getDynamicFilterOptions();
      } else if (tableStore.lastSelectedFilterOption) {
        const key = Object.keys(tableStore.lastSelectedFilterOption)[0];
        return {
          ...tableStore.getDynamicFilterOptions(),
          [key]: prevState[key],
        };
      }
      return tableStore.getDynamicFilterOptions();
    });
  }, [
    tableStore.activeTables.size,
    tableStore.filterStateChanged,
    hasShowUniqueIdsPermission,
    userSettingsStore.settings.table.limsX100,
    tableStore.selectedFilterOptions,
    tableStore.lastSelectedFilterOption,
    autoFilterOptions,
  ]);

  const getGameTypesLabelValue = (): string => {
    const selectedGameTypes = tableStore.selectedFilterOptions.gameTypes;
    if (selectedGameTypes.length === allGameTypes.length) {
      return 'All';
    } else if (!selectedGameTypes.length) {
      return 'None';
    } else if (selectedGameTypes.length > 2) {
      return `${selectedGameTypes[0]}, ${selectedGameTypes[1]}...`;
    } else {
      return tableStore.selectedFilterOptions.gameTypes.join(', ');
    }
  };

  const getCommonLabelValue = (target: string): string => {
    let selectedValues = tableStore.selectedFilterOptions[target];
    if (target === 'tableLimit') {
      selectedValues = commonStore.convertLimits(selectedValues);
    }
    if (selectedValues.length > 2) {
      return `${selectedValues[0]}, ${selectedValues[1]}...`;
    } else return selectedValues.join(', ');
  };

  const filteredOptions = useMemo(() => {
    if (!isChipsVisible) {
      return <></>;
    }
    const chips = [];
    if (tableStore.selectedFilterOptions.playerClub.length) {
      chips.push(
        <Chip
          key="player-club"
          className="me-1 mb-2"
          label={`Club: ${getCommonLabelValue('playerClub')}`}
          removable
          onRemove={() => setPlayerClub([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.playerLeague.length) {
      chips.push(
        <Chip
          key="player-league"
          className="me-1 mb-2"
          label={`League: ${getCommonLabelValue('playerLeague')}`}
          removable
          onRemove={() => setPlayerLeague([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.playerUniqueId.length) {
      chips.push(
        <Chip
          key="player-unique-id"
          className="me-1 mb-2"
          label={`Unique ID: ${getCommonLabelValue('playerUniqueId')}`}
          removable
          onRemove={() => setPlayerUniqueId([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.tableLimit.length) {
      chips.push(
        <Chip
          key="table-limit"
          className="me-1 mb-2"
          label={`Limit: ${getCommonLabelValue('tableLimit')}`}
          removable
          onRemove={() => setTableLimit([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.tableName.length) {
      chips.push(
        <Chip
          key="table-name"
          className="me-1 mb-2"
          label={`Name: ${getCommonLabelValue('tableName')}`}
          removable
          onRemove={() => setTableName([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.tableId.length) {
      chips.push(
        <Chip
          key="table-id"
          className="me-1 mb-2"
          label={`Table ID: ${getCommonLabelValue('tableId')}`}
          removable
          onRemove={() => setTableId([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.gameId.length) {
      chips.push(
        <Chip
          key="game-id"
          className="me-1 mb-2"
          label={`Game ID: ${getCommonLabelValue('gameId')}`}
          removable
          onRemove={() => setGameId([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.playerId.length) {
      chips.push(
        <Chip
          key="player-id"
          className="me-1 mb-2"
          label={`Player ID: ${getCommonLabelValue('playerId')}`}
          removable
          onRemove={() => updatePlayerId([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.sittingPlayers?.length) {
      chips.push(
        <Chip
          key="sitting-players"
          className="me-1 mb-2"
          label={`All players: ${getCommonLabelValue('sittingPlayers')}`}
          removable
          onRemove={() => setSittingPlayers([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.sittingBots?.length) {
      chips.push(
        <Chip
          key="sitting-bots"
          className="me-1 mb-2"
          label={`Bots: ${getCommonLabelValue('sittingBots')}`}
          removable
          onRemove={() => setSittingBots([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.sittingLivePlayers?.length) {
      chips.push(
        <Chip
          key="sitting-live-players"
          className="me-1 mb-2"
          label={`Live players: ${getCommonLabelValue('sittingLivePlayers')}`}
          removable
          onRemove={() => setSittingLivePlayers([])}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.onlyStartedGames) {
      chips.push(
        <Chip
          key="only-started-games"
          className="me-1 mb-2"
          label="Active games"
          removable
          onRemove={() =>
            tableStore.updateFilterOptions({ onlyStartedGames: false })
          }
        />,
      );
    }
    if (tableStore.selectedFilterOptions.onlyWithLivePlayers) {
      chips.push(
        <Chip
          key="only-with-live-players"
          className="me-1 mb-2"
          label="Live players"
          removable
          onRemove={() =>
            tableStore.updateFilterOptions({ onlyWithLivePlayers: false })
          }
        />,
      );
    }
    if (tableStore.selectedFilterOptions.onlyBots) {
      chips.push(
        <Chip
          key="only-bots"
          className="me-1 mb-2"
          label="Only bots"
          removable
          onRemove={() => tableStore.updateFilterOptions({ onlyBots: false })}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.autoOpenTables) {
      chips.push(
        <Chip
          key="auto-open-tables"
          className="me-1 mb-2"
          label="Auto-open tables"
          removable
          onRemove={() => {
            const trackedBotIds = [...tableStore.trackedBotIds];
            trackedBotIds.forEach(stopTrackingBot);
            tableStore.updateFilterOptions({ autoOpenTables: false });
          }}
        />,
      );
    }
    if (tableStore.selectedFilterOptions.autoCloseTables) {
      chips.push(
        <Chip
          key="auto-close-tables"
          className="me-1 mb-2"
          label="Auto-close tables"
          removable
          onRemove={() =>
            tableStore.updateFilterOptions({ autoCloseTables: false })
          }
        />,
      );
    }
    if (tableStore.selectedFilterOptions.hideFullTables) {
      chips.push(
        <Chip
          key="hide-full-tables"
          className="me-1 mb-2"
          label="Hide full tables"
          removable
          onRemove={() =>
            tableStore.updateFilterOptions({ hideFullTables: false })
          }
        />,
      );
    }
    chips.push(
      <Chip
        key="game-types"
        className="me-1 mb-2"
        label={`Game types: ${getGameTypesLabelValue()}`}
      />,
    );
    return <div className="filter-chips mt-3 mb-1">{chips}</div>;
  }, [
    tableStore.selectedFilterOptions,
    tableStore.trackedBotIds,
    userSettingsStore.settings.table.limsX100,
    isChipsVisible,
  ]);

  useEffect(() => {
    if (overlayRef.current && toggleEvent) {
      overlayRef.current.toggle(toggleEvent);
    }
  }, [toggleEvent]);

  const resetSelectedFilters = (): void => {
    tableStore.resetSelectedFilters();
    setTableId(tableStore.defaultSelectedFilterOptions.tableId);
    setGameId(tableStore.defaultSelectedFilterOptions.gameId);
    setPlayerId(tableStore.defaultSelectedFilterOptions.playerId);
    setPlayerClub(tableStore.defaultSelectedFilterOptions.playerClub);
    setPlayerLeague(tableStore.defaultSelectedFilterOptions.playerLeague);
    setPlayerUniqueId(tableStore.defaultSelectedFilterOptions.playerUniqueId);
    setTableName(tableStore.defaultSelectedFilterOptions.tableName);
    setTableLimit(tableStore.defaultSelectedFilterOptions.tableLimit);
    setSittingPlayers(tableStore.defaultSelectedFilterOptions.sittingPlayers);
    setSittingBots(tableStore.defaultSelectedFilterOptions.sittingBots);
    setSittingBots(tableStore.defaultSelectedFilterOptions.sittingLivePlayers);
  };

  const onSetAutoFilterOptions = (isAutoFilterOptions: boolean): void => {
    setAutoFilterOptions(isAutoFilterOptions);
    localStorageStore.setAutoFilterOptions(isAutoFilterOptions);
  };

  return (
    <>
      {filteredOptions}
      <OverlayPanel
        className="filter-overlay-panel p-3"
        ref={overlayRef}
        showCloseIcon
        data-ignore-outside-click
      >
        <div className="d-flex justify-content-between align-items-center my-2">
          <h2 className="m-0 ps-2">Filter</h2>
          <Button
            className="filter-reset-btn"
            label="Reset"
            rounded
            onClick={resetSelectedFilters}
          />
        </div>
        <div className="auto-filter-options d-flex gap-2 mb-3 ps-2 align-items-center">
          <Checkbox
            inputId="auto-filter-options"
            checked={autoFilterOptions}
            onChange={e => onSetAutoFilterOptions(e.checked)}
          />
          <label htmlFor="auto-filter-options">Auto filter options</label>
        </div>
        <div className="filter-options ps-2 pe-3">
          <div className="mb-3">
            <label className="fw-bold d-block mb-2" htmlFor="table-id">
              Table ID
            </label>
            <SortedMultiSelect
              id="table-id"
              placeholder="Enter table ID"
              value={tableId}
              onChange={e => setTableId(e.target.value)}
              options={filterOptions.tableId}
              filter
              maxSelectedLabels={3}
              className="w-100"
            />
          </div>
          <div className="mb-3">
            <label className="fw-bold d-block mb-2" htmlFor="table-name">
              Table name
            </label>
            <SortedMultiSelect
              id="table-name"
              placeholder="Enter table name"
              value={tableName}
              onChange={e => setTableName(e.target.value)}
              options={filterOptions.tableName}
              filter
              maxSelectedLabels={3}
              className="w-100"
            />
          </div>
          <div className="mb-3">
            <label className="fw-bold d-block mb-2" htmlFor="table-limit">
              Table limit
            </label>
            <SortedMultiSelect
              id="table-limit"
              placeholder="Enter table limit"
              value={tableLimit}
              onChange={e => setTableLimit(e.target.value)}
              options={filterOptions.tableLimit}
              filter
              maxSelectedLabels={3}
              className="w-100"
            />
          </div>
          <div className="mb-3">
            <label className="fw-bold d-block mb-2" htmlFor="game-id">
              Game ID
            </label>
            <SortedMultiSelect
              id="game-id"
              options={filterOptions.gameId}
              onChange={e => setGameId(e.target.value)}
              value={gameId}
              placeholder="Enter game ID"
              filter
              maxSelectedLabels={3}
              className="w-100"
            />
          </div>
          <div className="mb-3">
            <label className="fw-bold d-block mb-2" htmlFor="pid">
              Player ID
            </label>
            <SortedMultiSelect
              id="pid"
              placeholder="Enter player ID"
              value={playerId}
              onChange={e => updatePlayerId(e.target.value)}
              options={filterOptions.playerId}
              filter
              maxSelectedLabels={3}
              className="w-100"
            />
          </div>
          <div className="mb-3">
            <label className="fw-bold d-block mb-2" htmlFor="club">
              Player club
            </label>
            <SortedMultiSelect
              id="club"
              placeholder="Enter player club"
              value={playerClub}
              onChange={e => setPlayerClub(e.target.value)}
              options={filterOptions.playerClub}
              filter
              maxSelectedLabels={3}
              className="w-100"
            />
          </div>
          <div className="mb-3">
            <label className="fw-bold d-block mb-2" htmlFor="league">
              Player league
            </label>
            <SortedMultiSelect
              id="league"
              placeholder="Enter player league"
              value={playerLeague}
              onChange={e => setPlayerLeague(e.target.value)}
              options={filterOptions.playerLeague}
              filter
              maxSelectedLabels={3}
              className="w-100"
            />
          </div>
          {hasShowUniqueIdsPermission && (
            <div className="mb-3">
              <label className="fw-bold d-block mb-2" htmlFor="unique-id">
                Unique ID
              </label>
              <SortedMultiSelect
                id="unique-id"
                placeholder="Enter unique ID"
                value={playerUniqueId}
                onChange={e => setPlayerUniqueId(e.target.value)}
                options={filterOptions.playerUniqueId}
                filter
                maxSelectedLabels={3}
                className="w-100"
              />
            </div>
          )}
          <div className="mb-3">
            <label className="fw-bold d-block mb-2" htmlFor="game-type">
              Game type
            </label>
            <MultiSelect
              id="game-type"
              value={tableStore.selectedFilterOptions.gameTypes}
              onChange={e =>
                tableStore.updateFilterOptions({ gameTypes: e.value })
              }
              options={allGameTypes}
              optionLabel="key"
              maxSelectedLabels={3}
              className="w-100"
              placeholder="Select game type"
              filter
            />
          </div>
          <div className="mb-3">
            <label className="fw-bold d-block mb-2" htmlFor="sitting-players">
              Sitting all players
            </label>
            <MultiSelect
              id="sitting-players"
              value={sittingPlayers}
              onChange={e => setSittingPlayers(e.value)}
              options={PLAYERS_DIMENSION}
              optionLabel="label"
              optionValue="value"
              maxSelectedLabels={6}
              className="w-100"
              placeholder="Select sitting all players"
              filter
            />
          </div>
          <div className="mb-3">
            <label className="fw-bold d-block mb-2" htmlFor="sitting-bots">
              Sitting bots
            </label>
            <MultiSelect
              id="sitting-bots"
              value={sittingBots}
              onChange={e => setSittingBots(e.value)}
              options={PLAYERS_DIMENSION}
              optionLabel="label"
              optionValue="value"
              maxSelectedLabels={6}
              className="w-100"
              placeholder="Select sitting bots"
              filter
            />
          </div>
          <div className="mb-3">
            <label
              className="fw-bold d-block mb-2"
              htmlFor="sitting-live-players"
            >
              Sitting live players
            </label>
            <MultiSelect
              id="sitting-live-players"
              value={sittingLivePlayers}
              onChange={e => setSittingLivePlayers(e.value)}
              options={PLAYERS_DIMENSION}
              optionLabel="label"
              optionValue="value"
              maxSelectedLabels={6}
              className="w-100"
              placeholder="Select sitting live players"
              filter
            />
          </div>
          <div className="mb-3 d-flex align-items-center">
            <label className="fw-bold me-2" htmlFor="only-bots">
              Only bots:
            </label>
            <Checkbox
              inputId="only-bots"
              checked={tableStore.selectedFilterOptions.onlyBots}
              onChange={e =>
                tableStore.updateFilterOptions({
                  onlyBots: e.checked,
                  onlyWithLivePlayers:
                    tableStore.selectedFilterOptions.onlyWithLivePlayers &&
                    e.checked
                      ? false
                      : tableStore.selectedFilterOptions.onlyWithLivePlayers,
                })
              }
            />
          </div>
          <div className="mb-3 d-flex align-items-center">
            <label className="fw-bold me-2" htmlFor="live-players">
              Live players:
            </label>
            <Checkbox
              inputId="live-players"
              checked={tableStore.selectedFilterOptions.onlyWithLivePlayers}
              onChange={e =>
                tableStore.updateFilterOptions({
                  onlyWithLivePlayers: e.checked,
                  onlyBots:
                    tableStore.selectedFilterOptions.onlyBots && e.checked
                      ? false
                      : tableStore.selectedFilterOptions.onlyBots,
                })
              }
            />
          </div>
          <div className="mb-3 d-flex align-items-center">
            <label className="fw-bold me-2" htmlFor="game-started">
              Active games:
            </label>
            <Checkbox
              inputId="game-started"
              checked={tableStore.selectedFilterOptions.onlyStartedGames}
              onChange={e =>
                tableStore.updateFilterOptions({ onlyStartedGames: e.checked })
              }
            />
          </div>
          <div className="mb-3 d-flex align-items-center">
            <label className="fw-bold me-2" htmlFor="auto-open-tables">
              Auto-open tables:
            </label>
            <Checkbox
              inputId="auto-open-tables"
              checked={tableStore.selectedFilterOptions.autoOpenTables}
              onChange={e =>
                tableStore.updateFilterOptions({ autoOpenTables: e.checked })
              }
            />
          </div>
          <div className="mb-3 d-flex align-items-center">
            <label className="fw-bold me-2" htmlFor="auto-close-tables">
              Auto-close tables:
            </label>
            <Checkbox
              inputId="auto-close-tables"
              checked={tableStore.selectedFilterOptions.autoCloseTables}
              onChange={e =>
                tableStore.updateFilterOptions({ autoCloseTables: e.checked })
              }
            />
          </div>
          <div className="mb-3 d-flex align-items-center">
            <label className="fw-bold me-2" htmlFor="hide-full-tables">
              Hide full tables:
            </label>
            <Checkbox
              inputId="hide-full-tables"
              checked={tableStore.selectedFilterOptions.hideFullTables}
              onChange={e =>
                tableStore.updateFilterOptions({ hideFullTables: e.checked })
              }
            />
          </div>
        </div>
      </OverlayPanel>
    </>
  );
};

export default observer(Filter);
